import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeTable } from "./useRealtime";
import { levelForXp, XP_CAP, PRIORITY_XP } from "@/lib/xp";

export type UserStats = {
  user_id: string;
  xp: number;
  level: number;
  streak_days: number;
  streak_last_date: string | null;
  daily_completed: number;
  daily_completed_date: string | null;
  daily_target: number;
  total_completed: number;
  total_focus_minutes: number;
};

export function useUserStats() {
  useRealtimeTable("user_stats", ["user-stats"]);
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-stats", user?.id],
    queryFn: async () => {
      if (!user) return null;
      // Try to read existing row first
      const { data, error } = await supabase
        .from("user_stats")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      if (data) return data as UserStats;
      // No row yet — create one
      const { data: created, error: insertErr } = await supabase
        .from("user_stats")
        .insert({ user_id: user.id })
        .select()
        .single();
      if (insertErr) throw insertErr;
      return created as UserStats;
    },
    enabled: !!user,
  });
}

export function useAwardXP() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      baseXp,
      inFocusMode = false,
      itemPriority,
      itemXpValue,
    }: {
      baseXp?: number;
      inFocusMode?: boolean;
      itemPriority?: string;
      itemXpValue?: number | null;
    }) => {
      if (!user) throw new Error("Not authenticated");

      // 2026-05-17 evening MDT: per-item XP award now goes through the
      // atomic `award_xp_for_caller(p_xp_delta, p_count)` rpc (migration
      // 00066). Pre-fix this was a read-then-compute-then-write against
      // user_stats — two parallel awards could read the same xp and both
      // write their sum, silently losing one. Also used a client-side
      // `level = levelForXp(xp)` write that drifted from the PG-side curve.
      // Now: the bonus math (focus / streak / daily-target) is computed on
      // the cached stats client-side, then we ship the FINAL p_xp_delta to
      // PG which does the row-locked UPDATE and derives level via
      // public.level_for_xp inside the same lock window.

      const { data: stats, error: fetchErr } = await supabase
        .from("user_stats")
        .select("*")
        .eq("user_id", user.id)
        .single();
      if (fetchErr || !stats) throw new Error("Stats not found");

      let xp = itemXpValue ?? baseXp ?? PRIORITY_XP[itemPriority ?? "medium"] ?? 30;
      if (inFocusMode) xp = Math.floor(xp * 1.5);

      const today = new Date().toISOString().split("T")[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];

      // Projected streak the bonus will be calculated against. The actual
      // streak the DB lands on may differ if a parallel award races us, but
      // the bonus math is intentionally based on what the user CAN SEE in
      // the UI right now — keeps the feel of the game consistent.
      let projectedStreak = stats.streak_days;
      if (stats.streak_last_date === yesterday) {
        projectedStreak = stats.streak_days + 1;
      } else if (stats.streak_last_date !== today) {
        projectedStreak = 1;
      }
      const streakMultiplier = 1 + Math.min(projectedStreak * 0.1, 0.5);
      xp = Math.floor(xp * streakMultiplier);

      const projectedDaily =
        stats.daily_completed_date === today ? stats.daily_completed + 1 : 1;

      let dailyBonus = 0;
      if (
        projectedDaily === stats.daily_target &&
        (stats.daily_completed_date !== today || stats.daily_completed < stats.daily_target)
      ) {
        dailyBonus = 100;
      }

      const xpDelta = xp + dailyBonus;

      const { data: rpcRow, error } = await supabase.rpc("award_xp_for_caller", {
        p_xp_delta: xpDelta,
        p_count: 1,
      });
      if (error) throw error;

      const newRow = Array.isArray(rpcRow) ? rpcRow[0] : rpcRow;
      const newXp = Number(newRow?.xp ?? Math.min(stats.xp + xpDelta, XP_CAP));
      const newLevel = Number(newRow?.cur_level ?? levelForXp(newXp));
      const newStreak = Number(newRow?.streak_days ?? projectedStreak);
      const newDailyCompleted = Number(newRow?.daily_completed ?? projectedDaily);

      return {
        xpGained: xpDelta,
        newXp,
        newLevel,
        oldLevel: stats.level,
        leveledUp: newLevel > stats.level,
        streak: newStreak,
        dailyCompleted: newDailyCompleted,
        dailyTargetHit: newDailyCompleted === stats.daily_target,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
    },
  });
}
